import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    CircularProgress,
    Stack,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    Tooltip,
    Chip,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { CircularImage } from './CircularImage';

interface PodcastSponsorsProps {
    podcastId: string;
    selectedShowId: string;
    selectedShowTitle?: string;
    onSponsorSelect?: (sponsorId: string, sponsorName: string) => void;
    selectedSponsorId?: string;
    sponsors?: Sponsor[];
    podcastName?: string;
}

interface Sponsor {
    _id: string;
    name: string;
    logo?: string;
    website?: string;
    categories?: string[];
    mentionCount: number;
    episodeCount: number;
    totalViews?: number;
}

type Order = 'asc' | 'desc';
type OrderBy = 'name' | 'mentionCount' | 'episodeCount' | 'totalViews';

export const PodcastSponsors: React.FC<PodcastSponsorsProps> = ({
    podcastId,
    selectedShowId,
    selectedShowTitle,
    onSponsorSelect,
    selectedSponsorId,
    sponsors: propSponsors,
    podcastName,
}) => {
    const { t } = useTranslation('explorar');
    const [order, setOrder] = useState<Order>('desc');
    const [orderBy, setOrderBy] = useState<OrderBy>('totalViews');

    const { data: fetchedSponsors, isLoading: sponsorsLoading } = useQuery({
        queryKey: ['podcast-sponsors', selectedShowId],
        queryFn: () => apiService.getPodcastSponsors(selectedShowId === 'all' ? podcastId : selectedShowId),
        enabled: !!podcastId && !!selectedShowId && !propSponsors,
        staleTime: 0,
    });

    const sponsors = propSponsors || fetchedSponsors;

    // Sort sponsors based on current orderBy and order
    const sortedSponsors = React.useMemo(() => {
        if (!sponsors) return [];

        return [...sponsors].sort((a, b) => {
            let aValue: string | number = '';
            let bValue: string | number = '';

            switch (orderBy) {
                case 'name':
                    aValue = a.name;
                    bValue = b.name;
                    break;
                case 'mentionCount':
                    aValue = a.mentionCount || 0;
                    bValue = b.mentionCount || 0;
                    break;
                case 'episodeCount':
                    aValue = a.episodeCount || 0;
                    bValue = b.episodeCount || 0;
                    break;
                case 'totalViews':
                    aValue = a.totalViews || 0;
                    bValue = b.totalViews || 0;
                    break;
            }

            if (typeof aValue === 'string') {
                return order === 'asc'
                    ? aValue.localeCompare(bValue as string)
                    : (bValue as string).localeCompare(aValue);
            }

            return order === 'asc'
                ? (aValue as number) - (bValue as number)
                : (bValue as number) - (aValue as number);
        });
    }, [sponsors, orderBy, order]);

    const handleSort = (property: OrderBy) => {
        const newOrder = orderBy === property && order === 'desc' ? 'asc' : 'desc';
        setOrder(newOrder);
        setOrderBy(property);
    };

    if (sponsorsLoading && !propSponsors) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!sponsors || sponsors.length === 0) {
        return (
            <Stack spacing={2}>
                <Typography variant="h3">
                    {selectedShowId === 'all' || !selectedShowId
                        ? t('podcastSponsors.sponsors')
                        : t('podcastSponsors.sponsorsOf', { title: selectedShowTitle })}
                </Typography>
                <Typography color="text.secondary">
                    {t('podcastSponsors.empty')}
                </Typography>
            </Stack>
        );
    }

    return (
        <Stack spacing={2}>
            <Card>
                <CardContent>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'name'}
                                        direction={orderBy === 'name' ? order : 'asc'}
                                        onClick={() => handleSort('name')}
                                    >
                                        {t('podcastSponsors.sponsor')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">
                                    <TableSortLabel
                                        active={orderBy === 'mentionCount'}
                                        direction={orderBy === 'mentionCount' ? order : 'asc'}
                                        onClick={() => handleSort('mentionCount')}
                                    >
                                        {t('podcastSponsors.mentions')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">
                                    <TableSortLabel
                                        active={orderBy === 'episodeCount'}
                                        direction={orderBy === 'episodeCount' ? order : 'asc'}
                                        onClick={() => handleSort('episodeCount')}
                                    >
                                        {t('podcastSponsors.episodes')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">
                                    <TableSortLabel
                                        active={orderBy === 'totalViews'}
                                        direction={orderBy === 'totalViews' ? order : 'asc'}
                                        onClick={() => handleSort('totalViews')}
                                    >
                                        {t('podcastSponsors.views')}
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedSponsors.map(sponsor => (
                                <TableRow
                                    key={sponsor._id}
                                    hover
                                    onClick={() => {
                                        if (onSponsorSelect) {
                                            onSponsorSelect(sponsor._id, sponsor.name);
                                        }
                                    }}
                                    sx={{
                                        cursor: onSponsorSelect ? 'pointer' : 'default',
                                        backgroundColor:
                                            selectedSponsorId === sponsor._id
                                                ? 'action.selected'
                                                : 'inherit',
                                    }}
                                >
                                    <TableCell>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            {sponsor.logo && (
                                                <CircularImage
                                                    src={sponsor.logo}
                                                    alt={sponsor.name}
                                                    size={32}
                                                />
                                            )}
                                            <Box>
                                                <Typography variant="body1">
                                                    {sponsor.name}
                                                </Typography>
                                                {sponsor.categories && sponsor.categories.length > 0 && (
                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        sx={{ mt: 0.5 }}
                                                    >
                                                        {sponsor.categories.map((category: string, index: number) => (
                                                            <Chip
                                                                key={index}
                                                                label={category}
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                        ))}
                                                    </Stack>
                                                )}
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip
                                            title={t('podcastSponsors.mentionCount', {
                                                total: (sponsor.mentionCount || 0).toLocaleString(),
                                            })}
                                        >
                                            <Typography>
                                                {(sponsor.mentionCount || 0).toLocaleString()}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip
                                            title={t('podcastSponsors.episodeCount', {
                                                total: (sponsor.episodeCount || 0).toLocaleString(),
                                            })}
                                        >
                                            <Typography>
                                                {(sponsor.episodeCount || 0).toLocaleString()}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip
                                            title={t('podcastSponsors.viewCount', {
                                                total: (sponsor.totalViews || 0).toLocaleString(),
                                            })}
                                        >
                                            <Typography>
                                                {(sponsor.totalViews || 0).toLocaleString()}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </Stack>
    );
};
